import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { formatTime, getTodayTime, saveTodayTime, getTotalTime, getTotalTodayTime, getTodayKey } from '../utils/timeUtils';
import { useAuth } from '../context/AuthContext';
import { dataService } from '../services/dataService';
import type { VocabWord } from '../types/vocab';
import { fetchAllHardTerms } from '../services/hardTermsService';
import useIsMobile from '../hooks/useIsMobile';
import { useSessionTracker } from '../hooks/useSessionTracker';
import { useSubscription } from '../hooks/useSubscription';
import PaywallModal from './PaywallModal';
import { supabase } from '../services/supabaseClient';

const PRIMARY = 'var(--color-primary)';
const BORDER = 'var(--color-border)';
const SURFACE = 'var(--color-surface)';
const SURFACE_MUTED = 'var(--color-surface-muted)';
const TEXT_MUTED = 'var(--color-text-muted)';

const SESSION_SIZE = 10;

const cardBaseStyle: React.CSSProperties = {
  backgroundColor: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: '20px',
  padding: '24px 16px'
};

const answerButtonBase: React.CSSProperties = {
  width: '100%',
  borderRadius: '16px',
  border: `1px solid ${BORDER}`,
  backgroundColor: '#fff',
  padding: '13px 14px',
  minHeight: '56px',
  fontSize: '14px',
  fontWeight: 400,
  textAlign: 'left',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  cursor: 'pointer',
  color: 'var(--color-text)'
};

type WordData = VocabWord & { id?: string };

interface AnswerRecord {
  word: string;
  definition: string;
  isCorrect: boolean;
}

interface ReportRecord {
  word: string;
  type: 'definition' | 'synonym';
  reportedDefinition?: string;
  reportedSynonym?: string;
  note?: string;
  timestamp: number;
}

const HardTermsGame: React.FC = () => {
  const { user } = useAuth();
  const { isPro } = useSubscription();

  const FREE_HARD_TERMS_LIMIT = 25;
  const [showPaywall, setShowPaywall] = useState(false);
  const [totalWordCount, setTotalWordCount] = useState(0);
  useIsMobile(); // keep hook call for consistent hook order
  const [words, setWords] = useState<WordData[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [selectedAnswerText, setSelectedAnswerText] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [frozenOptions, setFrozenOptions] = useState<string[]>([]);
  const [correctAnswers, setCorrectAnswers] = useState<AnswerRecord[]>([]);
  const [incorrectAnswers, setIncorrectAnswers] = useState<AnswerRecord[]>([]);
  const [showBank, setShowBank] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [reviewMode, setReviewMode] = useState(false);
  const [jumpToNumber, setJumpToNumber] = useState<string>('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportNote, setReportNote] = useState('');
  const [reportedIssues, setReportedIssues] = useState<ReportRecord[]>([]);
  const [isShuffled, setIsShuffled] = useState(true);
  const [shuffleOrder, setShuffleOrder] = useState<number[] | null>(null);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchEndY = useRef<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [cardFlipRotation, setCardFlipRotation] = useState(0);
  const [nextCardIndex, setNextCardIndex] = useState<number | null>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [, setTodayTime] = useState(0);
  const [, setTotalTime] = useState(0);
  const [, setTotalTodayTime] = useState(0);
  const sessionStartTimeRef = useRef<number | null>(null);
  const todaySessionStartRef = useRef<number | null>(null);
  const initialSavedTimeRef = useRef<number>(0);
  const initialTodayTimeRef = useRef<number>(0);
  const pendingWordIndexRef = useRef<number | null>(null);
  const [showJumpSection, setShowJumpSection] = useState(false);

  const {
    isSessionActive,
    sessionNumber,
    lastSessionCardCount,
    lifetimeSeenCount,
    startSession,
    recordCardSeen,
    endSession,
  } = useSessionTracker('hardterms');

  // Distractor pool refs for Change 6
  const distractorPoolRef = useRef<string[]>([]);
  const distractorIndexRef = useRef(0);
  const recentCorrectRef = useRef<string[]>([]);

  const saveAnswerToSupabase = async (word: WordData, isCorrect: boolean) => {
    if (!user) return;
    try {
      await dataService.hardTermsAnswers.insert({
        user_id: user.id,
        word_id: word.ID || word.Word,
        word: word.Word,
        definition: word.Definition,
        correct: isCorrect,
        timestamp: Date.now(),
        date: getTodayKey()
      });
    } catch (e) {
      console.error('Error saving hard terms answer to Supabase:', e);
    }
  };

  const clearSupabaseProgress = async () => {
    if (!user) return;
    try {
      await Promise.all([
        dataService.hardTermsAnswers.deleteAll(user.id),
        dataService.reportedIssues.deleteByGameMode(user.id, 'hardterms'),
        dataService.userProgressState.delete(user.id, 'hardterms', 'last_word_index')
      ]);
    } catch (e) {
      console.error('Error clearing hard terms data from Supabase:', e);
    }
  };

  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear all saved progress? This cannot be undone.')) {
      return;
    }

    setCorrectAnswers([]);
    setIncorrectAnswers([]);
    setReportedIssues([]);
    try {
      localStorage.removeItem('gre_hard_terms_correct_answers');
      localStorage.removeItem('gre_hard_terms_incorrect_answers');
      localStorage.removeItem('gre_hard_terms_reported_issues');
      localStorage.removeItem('gre_hard_terms_last_word_index');
    } catch (e) {
      console.error('Error clearing hard terms data from localStorage:', e);
    }

    if (user) {
      await clearSupabaseProgress();
    }

    setShowBank(false);
  };

  // Load data from localStorage and Supabase when available
  useEffect(() => {
    let isMounted = true;

    const loadFromLocalStorage = () => {
      try {
        const savedCorrect = localStorage.getItem('gre_hard_terms_correct_answers');
        const savedIncorrect = localStorage.getItem('gre_hard_terms_incorrect_answers');
        const savedReported = localStorage.getItem('gre_hard_terms_reported_issues');
        const savedWordIndex = localStorage.getItem('gre_hard_terms_last_word_index');

        if (savedCorrect) {
          try {
            const parsed = JSON.parse(savedCorrect);
            if (isMounted) setCorrectAnswers(parsed);
          } catch (e) {
            console.error('Error parsing correct answers:', e);
          }
        }

        if (savedIncorrect) {
          try {
            const parsed = JSON.parse(savedIncorrect);
            if (isMounted) setIncorrectAnswers(parsed);
          } catch (e) {
            console.error('Error parsing incorrect answers:', e);
          }
        }

        if (savedReported) {
          try {
            const parsed = JSON.parse(savedReported);
            if (isMounted) setReportedIssues(parsed);
          } catch (e) {
            console.error('Error parsing reported issues from localStorage:', e);
          }
        }

        if (savedWordIndex) {
          const index = parseInt(savedWordIndex, 10);
          if (!isNaN(index) && index >= 0) {
            pendingWordIndexRef.current = index;
          }
        }

        const savedTime = localStorage.getItem('gre_hard_terms_time');
        if (savedTime) {
          try {
            const savedTimeSeconds = parseInt(savedTime, 10);
            if (!isNaN(savedTimeSeconds) && savedTimeSeconds >= 0) {
              initialSavedTimeRef.current = savedTimeSeconds;
              setElapsedTime(savedTimeSeconds);
            }
          } catch (e) {
            console.error('Error parsing saved time:', e);
          }
        }

        const todayTimeValue = getTodayTime('hardterms');
        initialTodayTimeRef.current = todayTimeValue;
        setTodayTime(todayTimeValue);
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
        sessionStartTimeRef.current = Date.now();
        todaySessionStartRef.current = Date.now();
      } catch (e) {
        console.error('Error accessing localStorage:', e);
      }
    };

    const loadFromSupabase = async () => {
      if (!user) return;
      try {
        const [
          { data: answersData, error: answersError },
          { data: reportsData, error: reportsError },
          { data: progressData, error: progressError }
        ] = await Promise.all([
          dataService.hardTermsAnswers.getAll(user.id),
          dataService.reportedIssues.getByGameMode(user.id, 'hardterms'),
          dataService.userProgressState.get(user.id, 'hardterms', 'last_word_index')
        ]);

        if (!isMounted) return;

        if (answersError) {
          console.error('Error loading hard terms answers from Supabase:', answersError.message);
        } else if (answersData) {
          const supaCorrect: AnswerRecord[] = [];
          const supaIncorrect: AnswerRecord[] = [];

          answersData.forEach((record: any) => {
            const answerRecord: AnswerRecord = {
              word: record.word,
              definition: record.definition || '',
              isCorrect: !!record.correct
            };
            if (record.correct) {
              supaCorrect.push(answerRecord);
            } else {
              supaIncorrect.push(answerRecord);
            }
          });

          setCorrectAnswers(supaCorrect);
          setIncorrectAnswers(supaIncorrect);
          try {
            localStorage.setItem('gre_hard_terms_correct_answers', JSON.stringify(supaCorrect));
            localStorage.setItem('gre_hard_terms_incorrect_answers', JSON.stringify(supaIncorrect));
          } catch (e) {
            console.error('Error caching hard terms answers locally:', e);
          }
        }

        if (reportsError) {
          console.error('Error loading hard terms reports from Supabase:', reportsError.message);
        } else if (reportsData) {
          const mappedReports: ReportRecord[] = reportsData.map((issue: any) => ({
            word: issue.word,
            type: 'definition',
            reportedDefinition: issue.reported_definition || undefined,
            note: issue.note || undefined,
            timestamp: issue.timestamp || Date.now()
          }));
          setReportedIssues(mappedReports);
          try {
            localStorage.setItem('gre_hard_terms_reported_issues', JSON.stringify(mappedReports));
          } catch (e) {
            console.error('Error caching hard terms reported issues:', e);
          }
        }

        if (progressError) {
          console.error('Error loading hard terms progress state:', progressError.message);
        } else if (progressData && progressData.state_value) {
          const index = parseInt(progressData.state_value, 10);
          if (!isNaN(index) && index >= 0) {
            pendingWordIndexRef.current = index;
          }
        }
      } catch (e) {
        console.error('Unexpected error loading hard terms data from Supabase:', e);
      }
    };

    loadFromLocalStorage();
    if (user) {
      loadFromSupabase();
    }

    return () => {
      isMounted = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Timer effect - update every second
  useEffect(() => {
    if (!sessionStartTimeRef.current) {
      sessionStartTimeRef.current = Date.now();
    }
    if (!todaySessionStartRef.current) {
      todaySessionStartRef.current = Date.now();
    }

    const interval = setInterval(() => {
      if (sessionStartTimeRef.current && todaySessionStartRef.current) {
        const now = Date.now();
        
        // Mode-specific timer - use initial saved time + current session elapsed
        const sessionElapsed = Math.floor((now - sessionStartTimeRef.current) / 1000);
        const totalElapsed = initialSavedTimeRef.current + sessionElapsed;
        setElapsedTime(totalElapsed);
        
        // Today's timer - use initial saved today time + current session elapsed
        const todaySessionElapsed = Math.floor((now - todaySessionStartRef.current) / 1000);
        const totalTodayElapsed = initialTodayTimeRef.current + todaySessionElapsed;
        setTodayTime(totalTodayElapsed);
        
        // Total time across all modes (recalculate from localStorage)
        setTotalTime(getTotalTime());
        setTotalTodayTime(getTotalTodayTime());
        
        // Save to localStorage every 5 seconds
        if (sessionElapsed % 5 === 0 && sessionElapsed > 0) {
          try {
            localStorage.setItem('gre_hard_terms_time', totalElapsed.toString());
            saveTodayTime('hardterms', totalTodayElapsed);
            // Update initial saved time to the new total, then reset session start
            initialSavedTimeRef.current = totalElapsed;
            initialTodayTimeRef.current = totalTodayElapsed;
            sessionStartTimeRef.current = Date.now();
            todaySessionStartRef.current = Date.now();
          } catch (e) {
            console.error('Error saving time to localStorage:', e);
          }
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Save both answers arrays to localStorage whenever they change
  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }
    
    try {
      localStorage.setItem('gre_hard_terms_correct_answers', JSON.stringify(correctAnswers));
      localStorage.setItem('gre_hard_terms_incorrect_answers', JSON.stringify(incorrectAnswers));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  }, [correctAnswers, incorrectAnswers, isInitialMount]);

  // Save current word index to localStorage whenever it changes
  useEffect(() => {
    if (words.length > 0 && currentWordIndex >= 0) {
      try {
        localStorage.setItem('gre_hard_terms_last_word_index', currentWordIndex.toString());
      } catch (e) {
        console.error('Error saving word index to localStorage:', e);
      }

      if (user) {
        dataService.userProgressState
          .upsert({
            user_id: user.id,
            game_mode: 'hardterms',
            state_key: 'last_word_index',
            state_value: currentWordIndex.toString()
          })
          .catch(err => console.error('Error saving hard terms word index to Supabase:', err));
      }
    }
  }, [currentWordIndex, words.length, user]);

  // Apply pending word index once words are loaded
  useEffect(() => {
    if (pendingWordIndexRef.current !== null && words.length > 0) {
      const clamped = Math.min(pendingWordIndexRef.current, words.length - 1);
      setCurrentWordIndex(clamped);
      pendingWordIndexRef.current = null;
    }
  }, [words.length]);

  // End session on unmount if active
  useEffect(() => {
    return () => {
      if (isSessionActive) endSession(lifetimeSeenCount);
    };
  }, [isSessionActive, endSession, lifetimeSeenCount]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetchAllHardTerms()
      .then(({ data, error }) => {
        if (!isMounted) return;
        if (error) {
          console.error('Error loading hard terms from Supabase:', error.message);
          setLoading(false);
          return;
        }
        if (!data || data.length === 0) {
          console.error('No hard terms found in Supabase.');
          setLoading(false);
          return;
        }
        const sorted = [...data].sort((a, b) => a.Word.localeCompare(b.Word));
        setTotalWordCount(sorted.length);
        const sortedData = isPro ? sorted : sorted.slice(0, FREE_HARD_TERMS_LIMIT);
        setWords(sortedData);
        setLoading(false);
        // Initialize distractor pool
        const pool = [...sortedData].sort(() => Math.random() - 0.5).map(w => w.Word);
        distractorPoolRef.current = pool;
        distractorIndexRef.current = 0;
        try {
          const savedWordIndex = localStorage.getItem('gre_hard_terms_last_word_index');
          if (savedWordIndex) {
            const index = parseInt(savedWordIndex, 10);
            if (!isNaN(index) && index >= 0 && index < sortedData.length) {
              setCurrentWordIndex(index);
            }
          }
        } catch (e) {
          console.error('Error loading saved word index:', e);
        }
      })
      .catch(err => {
        if (!isMounted) return;
        console.error('Unexpected error loading hard terms:', err);
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isPro]); // re-slice when subscription status changes

  // Base pool (review mode filter)
  const basePool = useMemo(() => {
    if (reviewMode && incorrectAnswers.length > 0) {
      const incorrectWords = incorrectAnswers.map(record => record.word);
      return words.filter(word => incorrectWords.includes(word.Word));
    }
    return [...words];
  }, [reviewMode, incorrectAnswers, words]);

  // Maintain a stable shuffle order so the correct answer stays aligned
  useEffect(() => {
    if (!isShuffled) {
      setShuffleOrder(null);
      return;
    }
    const length = basePool.length;
    if (!length) {
      setShuffleOrder(null);
      return;
    }
    if (shuffleOrder && shuffleOrder.length === length) return;
    const order = Array.from({ length }, (_, i) => i);
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [order[i], order[j]] = [order[j], order[i]];
    }
    setShuffleOrder(order);
  }, [isShuffled, basePool.length, shuffleOrder]);

  // Final word list respecting shuffle order
  const wordsToUse = useMemo(() => {
    if (isShuffled && shuffleOrder && shuffleOrder.length === basePool.length) {
      return shuffleOrder.map(idx => basePool[idx]);
    }
    return basePool;
  }, [isShuffled, shuffleOrder, basePool]);

  const getNextDistractorWord = useCallback((exclude: string[]): string => {
    const pool = distractorPoolRef.current;
    const recent = recentCorrectRef.current;
    for (let i = 0; i < pool.length; i++) {
      const idx = distractorIndexRef.current % pool.length;
      distractorIndexRef.current++;
      const candidate = pool[idx];
      if (!exclude.includes(candidate) && !recent.includes(candidate)) {
        return candidate;
      }
    }
    return pool.find(w => !exclude.includes(w)) ?? pool[0];
  }, []);

  // Generate multiple choice options
  const options = useMemo(() => {
    if (wordsToUse.length === 0 || !wordsToUse[currentWordIndex]) return [];
    if (frozenOptions.length > 0 && showResult) {
      return frozenOptions;
    }
    const currentWord = wordsToUse[currentWordIndex];
    const correctAnswer = currentWord.Definition;
    const correctWordStr = currentWord.Word;
    const wrongWords: string[] = [];
    const excludeSoFar = [correctWordStr];
    for (let i = 0; i < 3; i++) {
      const w = getNextDistractorWord(excludeSoFar);
      wrongWords.push(w);
      excludeSoFar.push(w);
    }
    const wrongAnswers = wrongWords.map(wWord => {
      const found = wordsToUse.find(w => w.Word === wWord) ?? words.find(w => w.Word === wWord);
      return found ? found.Definition : correctAnswer;
    });
    const allOptions = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    return allOptions;
  }, [wordsToUse, currentWordIndex, frozenOptions, showResult, getNextDistractorWord, words]);

  const currentWord = wordsToUse[currentWordIndex];

  const handleAnswerSelect = (index: number) => {
    if (showResult) return;
    
    const selectedOption = options[index];
    setSelectedAnswer(index);
    setSelectedAnswerText(selectedOption);
    setFrozenOptions([...options]);
    setShowResult(true);
    
    const isCorrect = selectedOption === currentWord.Definition;
    const record: AnswerRecord = {
      word: currentWord.Word,
      definition: currentWord.Definition,
      isCorrect
    };

    if (isCorrect) {
      recordCardSeen(currentWord.Word);
      recentCorrectRef.current = [currentWord.Word, ...recentCorrectRef.current].slice(0, 3);
      setCorrectAnswers(prev => {
        const exists = prev.some(r => r.word === currentWord.Word);
        if (exists) return prev;
        return [...prev, record];
      });
    } else {
      setIncorrectAnswers(prev => {
        const exists = prev.some(r => r.word === currentWord.Word);
        if (exists) return prev;
        return [...prev, record];
      });
    }

    // Award XP immediately per answer
    if (user) {
      supabase.rpc('record_session', {
        p_user_id: user.id,
        p_xp_earned: isCorrect ? 1 : 0,
        p_correct: isCorrect ? 1 : 0,
        p_answered: 1,
      }).then(({ error }) => {
        if (error) console.error('[XP] hard_terms record_session failed:', error.message);
      });
    }

    saveAnswerToSupabase(currentWord, isCorrect);
  };

  const nextWord = () => {
    // Free users hit the wall at the last card
    if (!isPro && currentWordIndex >= FREE_HARD_TERMS_LIMIT - 1 && totalWordCount > FREE_HARD_TERMS_LIMIT) {
      setShowPaywall(true);
      return;
    }
    setSwipeOffset(window.innerWidth);
    setCardFlipRotation(90);

    setTimeout(() => {
      setCurrentWordIndex(prev => (prev + 1) % wordsToUse.length);
      setSelectedAnswer(null);
      setSelectedAnswerText(null);
      setShowResult(false);
      setFrozenOptions([]);
      setSwipeOffset(0);
      setCardFlipRotation(0);
      setNextCardIndex(null);
    }, 300);
  };

  const previousWord = () => {
    setSwipeOffset(-window.innerWidth);
    setCardFlipRotation(-90);
    
    setTimeout(() => {
      setCurrentWordIndex(prev => (prev - 1 + wordsToUse.length) % wordsToUse.length);
      setSelectedAnswer(null);
      setSelectedAnswerText(null);
      setShowResult(false);
      setFrozenOptions([]);
      setSwipeOffset(0);
      setCardFlipRotation(0);
      setNextCardIndex(null);
    }, 300);
  };

  const jumpToCard = () => {
    const num = parseInt(jumpToNumber, 10);
    if (!isNaN(num) && num >= 1 && num <= wordsToUse.length) {
      setCurrentWordIndex(num - 1);
      setSelectedAnswer(null);
      setSelectedAnswerText(null);
      setShowResult(false);
      setFrozenOptions([]);
      setJumpToNumber('');
      setSwipeOffset(0);
      setCardFlipRotation(0);
      setNextCardIndex(null);
    }
  };

  const handleJumpInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      jumpToCard();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsSwiping(true);
    setSwipeOffset(0);
    setCardFlipRotation(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
    
    const deltaX = touchEndX.current - touchStartX.current;
    const deltaY = touchEndY.current - touchStartY.current;
    
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      setSwipeOffset(deltaX);
      setCardFlipRotation(deltaX / 10);
      
      if (deltaX > 50) {
        setNextCardIndex((currentWordIndex - 1 + wordsToUse.length) % wordsToUse.length);
      } else if (deltaX < -50) {
        setNextCardIndex((currentWordIndex + 1) % wordsToUse.length);
      }
    }
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    
    const deltaX = touchEndX.current - touchStartX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        previousWord();
      } else {
        nextWord();
      }
    } else {
      setSwipeOffset(0);
      setCardFlipRotation(0);
      setNextCardIndex(null);
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
    setIsSwiping(false);
  };

  const handleReportDefinition = () => {
    setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!currentWord) return;
    
    const report: ReportRecord = {
      word: currentWord.Word,
      type: 'definition',
      reportedDefinition: currentWord.Definition,
      note: reportNote,
      timestamp: Date.now()
    };
    
    setReportedIssues(prev => {
      const updated = [...prev, report];
      try {
        localStorage.setItem('gre_hard_terms_reported_issues', JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving report to localStorage:', e);
      }
      return updated;
    });

    if (user) {
      try {
        await dataService.reportedIssues.insert({
          user_id: user.id,
          game_mode: 'hardterms',
          word_id: currentWord.ID || currentWord.Word,
          word: currentWord.Word,
          note: reportNote || undefined,
          timestamp: report.timestamp,
          date: getTodayKey()
        });
      } catch (e) {
        console.error('Error saving hard terms report to Supabase:', e);
      }
    }
    
    setShowReportModal(false);
    setReportNote('');
  };

  const getOptionStyle = (index: number): React.CSSProperties => {
    if (!showResult) {
      return { ...answerButtonBase };
    }

    const option = options[index];
    const isSelected = index === selectedAnswer;
    const isCorrect = option === currentWord?.Definition;

    if (isSelected && isCorrect) {
      return {
        ...answerButtonBase,
        backgroundColor: 'var(--color-success-light)',
        borderColor: 'var(--color-success)',
        color: 'var(--color-success-text)',
        cursor: 'default',
      };
    } else if (isSelected && !isCorrect) {
      return {
        ...answerButtonBase,
        backgroundColor: 'var(--color-danger-light)',
        borderColor: 'var(--color-danger)',
        color: 'var(--color-danger-text)',
        cursor: 'default',
      };
    } else if (isCorrect) {
      return {
        ...answerButtonBase,
        backgroundColor: 'var(--color-success-light)',
        borderColor: 'var(--color-success)',
        color: 'var(--color-success-text)',
        cursor: 'default',
      };
    }

    return {
      ...answerButtonBase,
      opacity: 0.5,
      cursor: 'default',
    };
  };

  const accuracy = correctAnswers.length + incorrectAnswers.length > 0
    ? Math.round((correctAnswers.length / (correctAnswers.length + incorrectAnswers.length)) * 100)
    : 0;

  const totalAnswered = correctAnswers.length + incorrectAnswers.length;

  const progressPercent = Math.min(100, Math.round((lifetimeSeenCount / (wordsToUse.length || 1)) * 100));

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading Hard Terms...</p>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>No words loaded. Please check the CSV file.</p>
      </div>
    );
  }

  if (wordsToUse.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{
          backgroundColor: 'var(--color-success-light)',
          border: `1px solid var(--color-confirmation)`,
          borderRadius: '16px',
          padding: '15px',
          marginBottom: '15px'
        }}>
          <p style={{ fontSize: '16px', color: 'var(--color-success-text)', margin: 0 }}>
            Great job! You have no incorrect answers to review.
          </p>
        </div>
        <button
          onClick={() => setReviewMode(false)}
          className="btn-primary"
        >
          Back to Regular Quiz
        </button>
      </div>
    );
  }

  if (!isSessionActive) {
    return (
      <div style={{ padding: '24px 16px', fontFamily: 'inherit' }}>
        <div style={{
          backgroundColor: '#1F4C40',
          borderRadius: '20px',
          padding: '32px 24px',
          textAlign: 'center',
          color: '#FFFFFF',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🎯</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
            Session {sessionNumber}
          </div>
          {lastSessionCardCount > 0 && (
            <div style={{ fontSize: 13, color: '#9FE1CB', marginBottom: 16 }}>
              Last session: {lastSessionCardCount} cards
            </div>
          )}
          <div style={{ fontSize: 13, color: '#9FE1CB', marginBottom: 24 }}>
            {lifetimeSeenCount} / {wordsToUse.length} words seen lifetime
          </div>
          <button
            onClick={startSession}
            style={{
              backgroundColor: '#FFFFFF',
              color: '#1F4C40',
              border: 'none',
              borderRadius: 999,
              padding: '14px 40px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
            }}
          >
            Start session {sessionNumber}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'var(--color-background)', padding: '12px 16px' }}>
      {showPaywall && <PaywallModal feature="Full Hard Terms (all terms)" onClose={() => setShowPaywall(false)} />}
      <Helmet>
        <title>GRE Hard Terms Quiz – Difficult Vocabulary Practice | Phrenos</title>
        <meta name="description" content="Challenge yourself with the hardest GRE vocabulary. Multiple choice quiz with accuracy tracking, timed sessions, and instant feedback." />
        <meta property="og:title" content="GRE Hard Terms Quiz – Difficult Vocabulary Practice | Phrenos" />
        <meta property="og:description" content="Challenge yourself with the hardest GRE vocabulary. Multiple choice quiz with accuracy tracking, timed sessions, and instant feedback." />
        <meta property="og:url" content="https://phrenos.app/hard-terms" />
        <link rel="canonical" href="https://phrenos.app/hard-terms" />
      </Helmet>
      <div style={{ maxWidth: '1024px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <p className="eyebrow">Vocabulary</p>
        <h1 style={{ margin: 0 }}>{reviewMode ? 'Review Mode' : 'Hard Terms'}</h1>
      </div>

      {!isPro && (
        <button
          onClick={() => setShowPaywall(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px', borderRadius: 12,
            backgroundColor: 'var(--color-success-light)',
            border: '1px solid var(--color-primary)',
            color: 'var(--color-primary)', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          🔒 Free plan: {FREE_HARD_TERMS_LIMIT} of {totalWordCount} terms unlocked. Tap to upgrade.
        </button>
      )}

      {/* 4-metric stats row */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
        {[
          { label: 'Correct', value: correctAnswers.length, color: 'var(--color-success)' },
          { label: 'Incorrect', value: incorrectAnswers.length, color: 'var(--color-danger)' },
          { label: 'Accuracy', value: `${accuracy}%`, color: 'var(--color-confirmation)' },
          { label: 'Time', value: formatTime(elapsedTime), color: PRIMARY },
        ].map(stat => (
          <div key={stat.label} style={{
            flex: 1,
            backgroundColor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: '10px',
            padding: '6px 8px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '16px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '9px', color: TEXT_MUTED, marginTop: '2px' }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Session progress bar */}
      <div className="session-progress-bar" style={{ marginBottom: '10px' }}>
        <div className="session-progress-fill" style={{
          width: `${progressPercent}%`
        }} />
      </div>

      <div style={{ 
        position: 'relative',
        margin: '10px auto',
        perspective: '1000px',
        overflow: 'visible',
        minHeight: '250px',
        maxWidth: '90%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        {nextCardIndex !== null && wordsToUse[nextCardIndex] && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            border: `1px solid ${BORDER}`,
            borderRadius: 'var(--radius-card)',
            padding: '24px',
            backgroundColor: SURFACE,
            minHeight: '200px',
            zIndex: 0,
            transform: 'scale(0.97) translateY(8px)',
            opacity: 0.4,
            pointerEvents: 'none',
          }}>
            <h2 style={{ fontSize: '20px', margin: 0, color: TEXT_MUTED }}>
              Up next: "{wordsToUse[nextCardIndex]?.Word}"
            </h2>
          </div>
        )}
        
        <div
          ref={cardContainerRef}
          style={{
            ...cardBaseStyle,
            borderTop: `4px solid ${PRIMARY}`,
            position: 'relative',
            zIndex: 1,
            width: '100%',
            maxWidth: '95%',
            transform: `translateX(${swipeOffset}px) rotateY(${cardFlipRotation}deg)`,
            transformStyle: 'preserve-3d',
            opacity: isSwiping ? Math.max(0.3, 1 - Math.abs(cardFlipRotation) / 90) : 1,
            transition: isSwiping ? 'none' : 'transform 0.3s ease, opacity 0.3s ease',
            touchAction: 'pan-y',
            backfaceVisibility: 'hidden',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
        {/* Word pill badge */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <span style={{
            display: 'inline-block',
            backgroundColor: PRIMARY,
            color: '#fff',
            borderRadius: '999px',
            padding: '10px 28px',
            fontSize: '22px',
            fontWeight: 800,
            letterSpacing: '-0.5px',
          }}>
            {currentWord?.Word}
          </span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <p style={{ fontSize: '15px', color: TEXT_MUTED, margin: 0 }}>What does this word mean?</p>
          <button
            onClick={handleReportDefinition}
            style={{
              padding: '5px 10px',
              fontSize: '12px',
              cursor: 'pointer',
              backgroundColor: SURFACE_MUTED,
              color: 'var(--color-danger)',
              border: `1px solid ${BORDER}`,
              borderRadius: '999px',
              flexShrink: 0,
            }}
            title="Report incorrect definition"
          >
            Report
          </button>
        </div>

        <div style={{ marginTop: '8px' }}>
          {options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              style={{
                ...getOptionStyle(index),
                marginBottom: '10px',
              }}
              disabled={showResult}
            >
              <span style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                backgroundColor: 'transparent',
                border: `1.5px solid ${BORDER}`,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700, color: TEXT_MUTED,
                transition: 'all 0.15s ease',
              }}>
                {['A', 'B', 'C', 'D'][index]}
              </span>
              <span style={{ flex: 1 }}>{option}</span>
            </button>
          ))}
        </div>

        {showResult && (
          <>
            <div style={{
              marginTop: '15px',
              padding: '12px',
              borderRadius: '16px',
              border: '1px solid',
              backgroundColor: selectedAnswerText !== null && selectedAnswerText === currentWord.Definition
                ? 'var(--color-success-light)'
                : 'var(--color-danger-light)',
              borderColor: selectedAnswerText !== null && selectedAnswerText === currentWord.Definition
                ? 'var(--color-success)'
                : 'var(--color-danger)',
              color: selectedAnswerText !== null && selectedAnswerText === currentWord.Definition
                ? 'var(--color-success-text)'
                : 'var(--color-danger-text)',
            }}>
              {selectedAnswerText !== null && selectedAnswerText === currentWord.Definition ? (
                <p style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>✓ Correct!</p>
              ) : (
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>✗ Incorrect. The correct answer was: {currentWord.Definition}</p>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
              <button
                onClick={nextWord}
                style={{
                  padding: '12px 28px',
                  fontSize: '16px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  backgroundColor: 'var(--color-success)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '999px',
                  transition: 'transform 0.2s ease, background 0.2s ease',
                }}
              >
                Next Word →
              </button>
            </div>
          </>
        )}
        </div>
      </div>

      {/* Session progress bar */}
      <div style={{ marginTop: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ fontSize: '12px', color: TEXT_MUTED }}>
            Session {Math.floor(currentWordIndex / SESSION_SIZE) + 1} · {(currentWordIndex % SESSION_SIZE) + 1}/{SESSION_SIZE}
            {reviewMode && <span style={{ color: 'var(--color-danger)', marginLeft: 6 }}>· Review mode</span>}
          </span>
          <button
            onClick={() => setShowJumpSection(v => !v)}
            style={{ fontSize: '12px', color: TEXT_MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
          >
            {showJumpSection ? 'Hide jump ↑' : 'Jump to card ↓'}
          </button>
        </div>
        <div className="session-progress-bar">
          <div
            className="session-progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        {showJumpSection && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap' }}>
            <input
              id="jump-to-input-hard"
              type="number"
              min="1"
              max={wordsToUse.length}
              value={jumpToNumber}
              onChange={(e) => setJumpToNumber(e.target.value)}
              onKeyPress={handleJumpInputKeyPress}
              placeholder={`1–${wordsToUse.length}`}
              style={{
                padding: '6px 10px',
                fontSize: '13px',
                width: '90px',
                border: `1px solid ${BORDER}`,
                borderRadius: '10px',
                textAlign: 'center',
              }}
            />
            <button
              onClick={jumpToCard}
              disabled={!jumpToNumber || isNaN(parseInt(jumpToNumber, 10)) || parseInt(jumpToNumber, 10) < 1 || parseInt(jumpToNumber, 10) > wordsToUse.length}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                borderRadius: '999px',
                border: 'none',
                backgroundColor: PRIMARY,
                color: '#fff',
                cursor: 'pointer',
                opacity: (!jumpToNumber || isNaN(parseInt(jumpToNumber, 10)) || parseInt(jumpToNumber, 10) < 1 || parseInt(jumpToNumber, 10) > wordsToUse.length) ? 0.4 : 1,
                transition: 'opacity 0.15s ease',
              }}
            >
              Go
            </button>
          </div>
        )}
      </div>

      {/* Bottom action buttons */}
      <div style={{ marginTop: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          {incorrectAnswers.length > 0 && (
            <button
              onClick={() => setReviewMode(!reviewMode)}
              style={{
                padding: '12px 16px',
                fontSize: '14px',
                cursor: 'pointer',
                backgroundColor: reviewMode ? 'var(--color-success)' : 'var(--color-danger)',
                color: '#fff',
                border: 'none',
                borderRadius: '999px',
                fontWeight: 700,
                minHeight: 48,
              }}
            >
              {reviewMode ? 'Exit Review' : `Review Wrong (${incorrectAnswers.length})`}
            </button>
          )}
          <button
            onClick={() => setShowBank(true)}
            style={{
              padding: '12px 16px',
              fontSize: '14px',
              cursor: 'pointer',
              backgroundColor: SURFACE_MUTED,
              color: 'var(--color-text)',
              border: `1.5px solid ${BORDER}`,
              borderRadius: '999px',
              fontWeight: 500,
              minHeight: 48,
              gridColumn: incorrectAnswers.length > 0 ? undefined : '1 / -1',
            }}
          >
            View Bank ({totalAnswered})
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={() => { setIsShuffled(!isShuffled); setCurrentWordIndex(0); }}
            style={{
              padding: '7px 16px',
              fontSize: '13px',
              cursor: 'pointer',
              backgroundColor: isShuffled ? PRIMARY : SURFACE_MUTED,
              color: isShuffled ? '#fff' : 'var(--color-text)',
              border: 'none',
              borderRadius: '999px',
              fontWeight: 500,
            }}
          >
            {isShuffled ? '🔀 Shuffled' : '↕ Ordered'}
          </button>
        </div>
      </div>

      </div>{/* end maxWidth wrapper */}

      {/* Bank Modal */}
      {showBank && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Answer Bank</h3>
              <button
                onClick={() => setShowBank(false)}
                className="btn-secondary"
                style={{ minHeight: 'auto', padding: '6px 14px', fontSize: '13px' }}
              >
                Close
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: 'var(--color-success)', marginBottom: '10px', fontSize: '14px' }}>Correct ({correctAnswers.length})</h4>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {correctAnswers.map((record, index) => (
                  <div key={index} style={{
                    padding: '8px',
                    marginBottom: '5px',
                    backgroundColor: 'var(--color-success-light)',
                    border: `1px solid var(--color-confirmation)`,
                    borderRadius: '12px'
                  }}>
                    <strong>{record.word}</strong>: {record.definition}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 style={{ color: 'var(--color-danger)', marginBottom: '10px', fontSize: '14px' }}>Incorrect ({incorrectAnswers.length})</h4>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {incorrectAnswers.map((record, index) => (
                  <div key={index} style={{
                    padding: '8px',
                    marginBottom: '5px',
                    backgroundColor: 'var(--color-wrong-light)',
                    border: `1px solid var(--color-wrong-border)`,
                    borderRadius: '12px'
                  }}>
                    <strong>{record.word}</strong>: {record.definition}
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleClearData}
              className="btn-danger"
              style={{ marginTop: '20px', width: '100%' }}
            >
              Clear All Data
            </button>
            {reportedIssues.length > 0 && (
              <p style={{ marginTop: '10px', fontSize: '12px', color: TEXT_MUTED, textAlign: 'center' }}>
                Reported issues saved: {reportedIssues.length}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '25px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '20px' }}>
              Report Incorrect Definition
            </h3>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Word: <strong>{currentWord?.Word}</strong>
            </p>
            <p style={{ marginBottom: '15px', color: '#666' }}>
              Current Definition: <strong>{currentWord?.Definition}</strong>
            </p>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              What's wrong? (Optional)
            </label>
            <textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              placeholder="Please describe what's incorrect about this definition..."
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '12px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: '20px'
              }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportNote('');
                }}
                className="btn-secondary"
                style={{ minHeight: 'auto', padding: '10px 20px', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={submitReport}
                className="btn-danger"
                style={{ minHeight: 'auto', padding: '10px 20px', fontSize: '14px' }}
              >
                Submit Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HardTermsGame;

